import React, { useCallback, useEffect, useState } from 'react';
import { Dialog, Box, Typography, IconButton, Alert, CircularProgress, Collapse } from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';
import { getTeamSurveyHistory, deleteSurveyRound } from '../../../services/surveyService';
import { isAdmin } from '../../../services/authService';
import { A, APill, ABtn, Cap } from './reviewUi';
import { SurveyScores } from './RetroPanels';

const RETRO_GROUPS = [['start', 'Start'], ['stop', 'Stop'], ['continue', 'Continue']];
const shortDate = (d) => (d ? new Date(d).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) : '—');

/**
 * Anonymous Start / Stop / Continue text grouped by box. With onTrack, each line gets a
 * Track button that turns it into a retro action; lines already in `tracked` show as tracked.
 */
export const RetroLists = ({ retro, onTrack, tracked = [], busyText }) => {
  if (!retro) return null;
  if (retro.hidden) {
    return <Cap>No responses yet.</Cap>;
  }
  const isTracked = (text) => tracked.some((t) => t.toLowerCase() === text.toLowerCase());
  const empty = RETRO_GROUPS.every(([key]) => !retro[key]?.length);
  if (empty) return <Cap>No one wrote anything in Start, Stop or Continue.</Cap>;
  return (
    <Box sx={{ display: 'grid', gap: 1.5 }}>
      {RETRO_GROUPS.map(([key, label]) => (
        <Box key={key}>
          <Typography component="h4" sx={{ fontSize: 13, fontWeight: 600, color: A.ink, mb: 0.5 }}>
            {label} <Box component="span" sx={{ color: A.ink3, fontWeight: 400 }}>({retro[key].length})</Box>
          </Typography>
          {retro[key].length === 0 && <Cap>Nothing written.</Cap>}
          <Box component="ul" sx={{ m: 0, pl: 2.25, display: 'grid', gap: 0.5 }}>
            {retro[key].map((text, k) => (
              <Box component="li" key={k} sx={{ fontSize: 13.5, color: A.ink2, whiteSpace: 'pre-wrap' }}>
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start', justifyContent: 'space-between' }}>
                  <span>{text}</span>
                  {onTrack && (isTracked(text)
                    ? <APill tone="ok">Tracked</APill>
                    : <ABtn sx={{ height: 26, px: 1, fontSize: 12, flexShrink: 0 }} disabled={busyText === text} onClick={() => onTrack(text)}>Track</ABtn>)}
                </Box>
              </Box>
            ))}
          </Box>
        </Box>
      ))}
    </Box>
  );
};

const RoundRow = ({ round, admin, onDelete, deleting }) => {
  const [open, setOpen] = useState(false);
  const [confirm, setConfirm] = useState(false);
  const sum = round.summary;
  const score = sum && !sum.hidden ? sum.overallMean.toFixed(1) : '—';
  return (
    <Box sx={{ border: `1px solid ${A.line}`, borderRadius: '10px', p: 1.5 }}>
      <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center', flexWrap: 'wrap' }}>
        <Box sx={{ flex: 1, minWidth: 180 }}>
          <Typography sx={{ fontSize: 14.5, fontWeight: 600, color: A.ink }}>{round.sprintName}</Typography>
          <Cap>Opened {shortDate(round.openedAt)}{round.openedBy ? ` by ${round.openedBy}` : ''} · {round.responseCount} {round.responseCount === 1 ? 'response' : 'responses'}</Cap>
        </Box>
        <APill tone={round.status === 'open' ? 'warn' : 'none'}>{round.status === 'open' ? 'Open' : 'Closed'}</APill>
        <Box sx={{ textAlign: 'right', minWidth: 52 }} title="Average of all questions, 1 to 5">
          <Typography sx={{ fontSize: 18, fontWeight: 600, color: A.ink }}>{score}</Typography>
        </Box>
        <ABtn aria-expanded={open} onClick={() => setOpen((o) => !o)}>{open ? 'Hide' : 'Show'}</ABtn>
        {admin && !confirm && <ABtn onClick={() => setConfirm(true)}>Delete</ABtn>}
      </Box>
      {admin && confirm && (
        <Alert severity="warning" sx={{ mt: 1.25 }} action={(
          <Box sx={{ display: 'flex', gap: 1 }}>
            <ABtn disabled={deleting} onClick={() => setConfirm(false)}>Cancel</ABtn>
            <ABtn variant="primary" disabled={deleting} onClick={() => onDelete(round)}>{deleting ? 'Deleting…' : 'Delete survey'}</ABtn>
          </Box>
        )}>
          Delete the {round.sprintName} survey and its {round.responseCount} {round.responseCount === 1 ? 'response' : 'responses'}? This can't be undone.
        </Alert>
      )}
      <Collapse in={open} unmountOnExit>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2.5, mt: 1.5 }}>
          <Box>
            <Typography component="h4" sx={{ fontSize: 13, fontWeight: 600, color: A.ink, mb: 0.5 }}>Scores</Typography>
            <SurveyScores c={{}} survey={{ summary: sum, status: round.status }} dense />
          </Box>
          <RetroLists retro={round.retro} />
        </Box>
      </Collapse>
    </Box>
  );
};

/** Every anonymous survey the team has run, newest first. Admins can delete a survey and its responses. */
const SurveyHistoryDialog = ({ open, onClose, team, teamName, onChanged }) => {
  const [rounds, setRounds] = useState(null);
  const [error, setError] = useState('');
  const [deletingId, setDeletingId] = useState(null);
  const admin = isAdmin();

  const load = useCallback(async () => {
    setError('');
    try { setRounds((await getTeamSurveyHistory(team)).rounds); } catch (err) { setError(err.message); setRounds([]); }
  }, [team]);

  useEffect(() => {
    if (!open) return;
    setRounds(null);
    load();
  }, [open, load]);

  const remove = async (round) => {
    setDeletingId(round.id);
    setError('');
    try {
      await deleteSurveyRound(round.id);
      await load();
      if (onChanged) await onChanged();
    } catch (err) {
      setError(err.message);
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md" aria-labelledby="survey-history-title">
      <Box sx={{ p: { xs: 2, sm: 3 } }}>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
          <Box sx={{ flex: 1 }}>
            <Typography id="survey-history-title" component="h2" sx={{ fontSize: 20, fontWeight: 600, color: A.ink }}>
              Surveys{teamName ? ` · ${teamName}` : ''}
            </Typography>
            <Cap>One anonymous survey per sprint, with its scores and Start / Stop / Continue answers.</Cap>
          </Box>
          <IconButton aria-label="Close" onClick={onClose} size="small"><CloseIcon fontSize="small" /></IconButton>
        </Box>
        {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
        {rounds === null && <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress size={28} /></Box>}
        {rounds && rounds.length === 0 && !error && <Cap sx={{ mt: 2 }}>No surveys yet. Start one from the Sprint retro section.</Cap>}
        {rounds && rounds.length > 0 && (
          <Box sx={{ display: 'grid', gap: 1.25, mt: 2 }}>
            {rounds.map((r) => <RoundRow key={r.id} round={r} admin={admin} onDelete={remove} deleting={deletingId === r.id} />)}
          </Box>
        )}
      </Box>
    </Dialog>
  );
};

export default SurveyHistoryDialog;
